import { v4 as uuid } from "uuid";
import type {
    LomdaChapter,
    LomdaScene,
} from "@/features/lomdaEditorV2/lib/lomdaTypes";

const makeSingletonChapter = (sceneId: string): LomdaChapter => ({
    id: uuid(),
    title: "",
    sceneIds: [sceneId],
});

/**
 * Returns a chapters list that satisfies all invariants:
 *  - every scene belongs to exactly one chapter
 *  - chapters contain only sceneIds that exist
 *  - chapters are non-empty (empty ones are dropped)
 *  - duplicate sceneId references across chapters are de-duped (first wins)
 *  - when input is empty/undefined: ONE chapter containing every scene in
 *    the scenes-array order. (The user splits where they want boundaries.)
 *
 * Order is treated as caller-intent: this function NEVER reorders sceneIds
 * within a chapter or chapters between each other. The chapter list is the
 * source of truth for ordering; the scenes array is rebuilt to match it by
 * the callers (writeChapters, addScene, reorderScenes, provider init).
 */
export function normalizeChapters(
    scenes: LomdaScene[],
    chapters: LomdaChapter[] | undefined
): LomdaChapter[] {
    const sceneIds = new Set(scenes.map((s) => s.id));

    if (!chapters || chapters.length === 0) {
        if (scenes.length === 0) return [];
        return [
            {
                id: uuid(),
                title: "",
                sceneIds: scenes.map((s) => s.id),
            },
        ];
    }

    const seen = new Set<string>();
    const cleaned: LomdaChapter[] = [];
    for (const ch of chapters) {
        const ids: string[] = [];
        for (const id of ch.sceneIds) {
            if (!sceneIds.has(id)) continue;
            if (seen.has(id)) continue;
            seen.add(id);
            ids.push(id);
        }
        if (ids.length === 0) continue;
        cleaned.push({
            id: ch.id || uuid(),
            title: ch.title ?? "",
            sceneIds: ids,
        });
    }

    for (const s of scenes) {
        if (!seen.has(s.id)) {
            cleaned.push(makeSingletonChapter(s.id));
            seen.add(s.id);
        }
    }

    return cleaned;
}

/**
 * Flatten chapters into a single ordered sceneId list (used by callers to
 * rebuild the scenes array so its order matches the chapter source of truth).
 */
export function flattenChapterSceneIds(chapters: LomdaChapter[]): string[] {
    const out: string[] = [];
    for (const c of chapters) out.push(...c.sceneIds);
    return out;
}

export function getChapterForScene(
    chapters: LomdaChapter[],
    sceneId: string
): LomdaChapter | null {
    return chapters.find((c) => c.sceneIds.includes(sceneId)) ?? null;
}

export function getChapterIndexForScene(
    chapters: LomdaChapter[],
    sceneId: string
): number {
    return chapters.findIndex((c) => c.sceneIds.includes(sceneId));
}

export function renameChapter(
    chapters: LomdaChapter[],
    chapterId: string,
    title: string
): LomdaChapter[] {
    return chapters.map((c) => (c.id === chapterId ? { ...c, title } : c));
}

/**
 * Move `sceneId` to the end of the chapter directly above the one it's in.
 * No-op when the scene is already in the first chapter. If this empties the
 * source chapter (single-scene case), the source chapter is dropped — which
 * is how the single-scene case effectively becomes a chapter merge.
 */
export function moveSceneToPreviousChapter(
    chapters: LomdaChapter[],
    sceneId: string
): LomdaChapter[] {
    const chIdx = chapters.findIndex((c) => c.sceneIds.includes(sceneId));
    if (chIdx <= 0) return chapters;
    const source = chapters[chIdx];
    const prev = chapters[chIdx - 1];
    const next = [...chapters];
    next[chIdx - 1] = {
        ...prev,
        sceneIds: [...prev.sceneIds, sceneId],
    };
    const remaining = source.sceneIds.filter((id) => id !== sceneId);
    if (remaining.length === 0) {
        next.splice(chIdx, 1);
    } else {
        next[chIdx] = { ...source, sceneIds: remaining };
    }
    return next;
}

/**
 * Move `sceneId` to the start of the chapter directly below the one it's in.
 * No-op when the scene is already in the last chapter. If this empties the
 * source chapter (single-scene case), the source chapter is dropped — which
 * is how the single-scene case effectively becomes a chapter merge.
 */
export function moveSceneToNextChapter(
    chapters: LomdaChapter[],
    sceneId: string
): LomdaChapter[] {
    const chIdx = chapters.findIndex((c) => c.sceneIds.includes(sceneId));
    if (chIdx === -1 || chIdx >= chapters.length - 1) return chapters;
    const source = chapters[chIdx];
    const nextCh = chapters[chIdx + 1];
    const next = [...chapters];
    next[chIdx + 1] = {
        ...nextCh,
        sceneIds: [sceneId, ...nextCh.sceneIds],
    };
    const remaining = source.sceneIds.filter((id) => id !== sceneId);
    if (remaining.length === 0) {
        next.splice(chIdx, 1);
    } else {
        next[chIdx] = { ...source, sceneIds: remaining };
    }
    return next;
}

/**
 * Isolate a scene into its own brand-new chapter. The isolated scene always
 * gets a fresh chapter id with an empty title; the original chapter id/title
 * stays with whichever side has the remaining scenes (preferring the "before"
 * side when both exist, since that's where the chapter naturally continues).
 *
 *   [s1, s2, s3] isolate s2 → [s1] | [s2] | [s3]   (orig stays on [s1])
 *   [s1, s2, s3] isolate s1 → [s1] | [s2, s3]      (orig stays on [s2, s3])
 *   [s1, s2, s3] isolate s3 → [s1, s2] | [s3]      (orig stays on [s1, s2])
 *
 * No-op when the scene is already alone in its chapter.
 */
export function isolateSceneAsChapter(
    chapters: LomdaChapter[],
    sceneId: string
): LomdaChapter[] {
    const chIdx = chapters.findIndex((c) => c.sceneIds.includes(sceneId));
    if (chIdx === -1) return chapters;
    const ch = chapters[chIdx];
    if (ch.sceneIds.length === 1) return chapters;
    const sIdx = ch.sceneIds.indexOf(sceneId);
    const before = ch.sceneIds.slice(0, sIdx);
    const after = ch.sceneIds.slice(sIdx + 1);

    const isolated: LomdaChapter = {
        id: uuid(),
        title: "",
        sceneIds: [sceneId],
    };
    const result: LomdaChapter[] = [];
    if (before.length > 0) {
        result.push({ ...ch, sceneIds: before });
        result.push(isolated);
        if (after.length > 0) {
            result.push({ id: uuid(), title: "", sceneIds: after });
        }
    } else {
        // Isolated scene was first: the original chapter id/title rides along
        // with the remainder (which is guaranteed non-empty since length > 1).
        result.push(isolated);
        result.push({ ...ch, sceneIds: after });
    }
    return [
        ...chapters.slice(0, chIdx),
        ...result,
        ...chapters.slice(chIdx + 1),
    ];
}

/** Split the chapter containing `sceneId` so that `sceneId` becomes the first scene of a new chapter. No-op when it's already first. */
export function splitAt(
    chapters: LomdaChapter[],
    sceneId: string
): LomdaChapter[] {
    const chIdx = chapters.findIndex((c) => c.sceneIds.includes(sceneId));
    if (chIdx === -1) return chapters;
    const ch = chapters[chIdx];
    const sIdx = ch.sceneIds.indexOf(sceneId);
    if (sIdx <= 0) return chapters;
    const before: LomdaChapter = {
        ...ch,
        sceneIds: ch.sceneIds.slice(0, sIdx),
    };
    const after: LomdaChapter = {
        id: uuid(),
        title: "",
        sceneIds: ch.sceneIds.slice(sIdx),
    };
    const next = [...chapters];
    next.splice(chIdx, 1, before, after);
    return next;
}

/** Move chapter from one position to another, carrying all its scenes as a unit. */
export function reorderChapters(
    chapters: LomdaChapter[],
    fromIndex: number,
    toIndex: number
): LomdaChapter[] {
    if (fromIndex === toIndex) return chapters;
    const next = [...chapters];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
}

/**
 * Move a scene into a target chapter. If `beforeSceneId` is provided the
 * scene lands directly before it; otherwise it's appended to the end of
 * the target chapter. Empty source chapters are dropped.
 */
export function moveSceneToChapter(
    chapters: LomdaChapter[],
    sceneId: string,
    targetChapterId: string,
    beforeSceneId?: string
): LomdaChapter[] {
    const next: LomdaChapter[] = [];
    for (const c of chapters) {
        if (c.sceneIds.includes(sceneId)) {
            const remaining = c.sceneIds.filter((id) => id !== sceneId);
            if (remaining.length > 0) {
                next.push({ ...c, sceneIds: remaining });
            }
        } else {
            next.push(c);
        }
    }
    const targetIdx = next.findIndex((c) => c.id === targetChapterId);
    if (targetIdx === -1) {
        next.push({ id: uuid(), title: "", sceneIds: [sceneId] });
        return next;
    }
    const target = next[targetIdx];
    let insertAt = target.sceneIds.length;
    if (beforeSceneId) {
        const i = target.sceneIds.indexOf(beforeSceneId);
        if (i !== -1) insertAt = i;
    }
    const nextSceneIds = [...target.sceneIds];
    nextSceneIds.splice(insertAt, 0, sceneId);
    next[targetIdx] = { ...target, sceneIds: nextSceneIds };
    return next;
}

/** Remove a scene from whatever chapter contains it; drops empty chapters. */
export function removeSceneFromAllChapters(
    chapters: LomdaChapter[],
    sceneId: string
): LomdaChapter[] {
    const next: LomdaChapter[] = [];
    for (const c of chapters) {
        if (!c.sceneIds.includes(sceneId)) {
            next.push(c);
            continue;
        }
        const remaining = c.sceneIds.filter((id) => id !== sceneId);
        if (remaining.length > 0) next.push({ ...c, sceneIds: remaining });
    }
    return next;
}

/**
 * Insert a new sceneId immediately after `afterSceneId` inside the chapter
 * that contains it. When `afterSceneId` is null/not found, append to the
 * last chapter (creating one if no chapters exist).
 */
export function insertSceneAfter(
    chapters: LomdaChapter[],
    newSceneId: string,
    afterSceneId: string | null
): LomdaChapter[] {
    if (chapters.length === 0) {
        return [{ id: uuid(), title: "", sceneIds: [newSceneId] }];
    }
    if (afterSceneId) {
        const chIdx = chapters.findIndex((c) =>
            c.sceneIds.includes(afterSceneId)
        );
        if (chIdx !== -1) {
            const ch = chapters[chIdx];
            const sIdx = ch.sceneIds.indexOf(afterSceneId);
            const nextSceneIds = [...ch.sceneIds];
            nextSceneIds.splice(sIdx + 1, 0, newSceneId);
            const next = [...chapters];
            next[chIdx] = { ...ch, sceneIds: nextSceneIds };
            return next;
        }
    }
    const lastIdx = chapters.length - 1;
    const last = chapters[lastIdx];
    const next = [...chapters];
    next[lastIdx] = { ...last, sceneIds: [...last.sceneIds, newSceneId] };
    return next;
}
